import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { IoAdd, IoClose, IoCopyOutline } from "react-icons/io5";
import CustomNavbar from "../../components/common/CustomNavbar";
import PageLayout from "../../components/layouts/PageLayout";
import ECSLoader from "../../components/common/EcsLoader";
import customFetch from "../../util/config/axiosConfig";

interface Access {
  allowed: boolean;
  reason?: string | null;
  endpoint: string;
  maxKeys: number;
}
interface ApiKey {
  id: number;
  name: string;
  prefix: string;
  active: boolean;
  createdAt?: number;
  lastUsedAt?: number | null;
}
interface Campaign {
  id: number;
  name: string;
  templateName: string;
  language: string;
  status: "LIVE" | "PAUSED";
  defaultMediaUrl?: string | null;
  sentCount: number;
  failedCount: number;
  lastSentAt?: number | null;
}
interface MetaTemplate {
  name: string;
  language: string;
  status?: string;
  category?: string;
}

const msg = (e: any) =>
  e?.response?.data?.error?.message || e?.response?.data?.message || e?.message || "Something went wrong";
const data = (res: any) => res?.data?.data;

const INPUT = "w-full rounded-xl border border-gray-200 px-3.5 py-2.5 text-sm outline-none focus:border-brand-main";
const BTN_MAIN =
  "inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-4xl bg-brand-blue-gradient text-white font-bold text-sm disabled:opacity-50";
const BTN_PLAIN =
  "inline-flex items-center justify-center gap-2 px-4 py-2 rounded-4xl border border-gray-200 text-sm font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-50";
const CARD = "bg-white rounded-4xl p-7 flex flex-col gap-5";

const when = (ms?: number | null) => (ms ? new Date(ms).toLocaleString() : "Never");

/** The full URL customers call: the API base URL + the endpoint path. */
const apiUrl = (endpoint: string) =>
  endpoint.startsWith("http") ? endpoint : `${(process.env.REACT_APP_BASE_URL || "").replace(/\/$/, "")}${endpoint}`;

/**
 * Self-serve API (AiSensy style): API keys and API campaigns. Customers call POST /api/v2/campaign with
 * the key, a campaign name and the number; the older company-token API is still under "Developer".
 */
export default function ApiKeys() {
  const navigate = useNavigate();
  const [access, setAccess] = useState<Access | null>(null);
  const [keys, setKeys] = useState<ApiKey[]>([]);
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [newKey, setNewKey] = useState<string | null>(null);
  const [creatingCampaign, setCreatingCampaign] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const a = data(await customFetch.get("/api/developer/access")) as Access;
      setAccess(a);
      const [k, c] = await Promise.all([customFetch.get("/api/developer/keys"), customFetch.get("/api/developer/campaigns")]);
      setKeys(data(k) || []);
      setCampaigns(data(c) || []);
    } catch (e) {
      toast.error(msg(e));
      setAccess((x) => x || { allowed: false, reason: msg(e), endpoint: "/api/v2/campaign", maxKeys: 5 });
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  async function createKey() {
    const name = window.prompt("Name this key (for example: Website, CRM)", "API key");
    if (name === null) return;
    setBusy(true);
    try {
      const res = data(await customFetch.post("/api/developer/keys", { name }));
      setNewKey(res.apiKey);
      await load();
    } catch (e) {
      toast.error(msg(e));
    } finally {
      setBusy(false);
    }
  }

  async function revoke(k: ApiKey) {
    if (!window.confirm(`Revoke "${k.name}"? Systems using it will stop sending immediately.`)) return;
    try {
      await customFetch.delete(`/api/developer/keys/${k.id}`);
      toast.success("API key revoked");
      await load();
    } catch (e) {
      toast.error(msg(e));
    }
  }

  async function toggle(c: Campaign) {
    try {
      await customFetch.put(`/api/developer/campaigns/${c.id}`, { status: c.status === "LIVE" ? "PAUSED" : "LIVE" });
      await load();
    } catch (e) {
      toast.error(msg(e));
    }
  }

  async function removeCampaign(c: Campaign) {
    if (!window.confirm(`Delete the campaign "${c.name}"? API calls using this name will fail.`)) return;
    try {
      await customFetch.delete(`/api/developer/campaigns/${c.id}`);
      await load();
    } catch (e) {
      toast.error(msg(e));
    }
  }

  const example = useMemo(() => {
    const c = campaigns[0];
    return `curl -X POST '${apiUrl(access?.endpoint || "/api/v2/campaign")}' \\
  -H 'Content-Type: application/json' \\
  -d '{
    "apiKey": "YOUR_API_KEY",
    "campaignName": "${c?.name || "your_campaign_name"}",
    "destination": "919876543210",
    "userName": "Asha",
    "templateParams": ["Asha", "#1042"],
    "media": { "url": "https://example.com/invoice.pdf", "filename": "invoice.pdf" },
    "tags": ["website"]
  }'`;
  }, [access, campaigns]);

  return (
    <PageLayout
      customNavbar={
        <CustomNavbar>
          <div className="flex gap-3 items-center w-max">
            <span className="font-bold text-lg uppercase">API keys</span>
          </div>
        </CustomNavbar>
      }
    >
      {access === null ? (
        <div className={CARD}>
          <ECSLoader />
        </div>
      ) : !access.allowed ? (
        <div className={CARD}>
          <h2 className="text-2xl font-bold text-gray-800">WhatsApp API</h2>
          <p className="text-sm text-gray-500">{access.reason}</p>
          <div>
            <button className={BTN_MAIN} onClick={() => navigate("/admin/pricing")}>
              See plans
            </button>
          </div>
        </div>
      ) : (
        <div className="flex flex-col gap-6">
          <div className={CARD}>
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
              <div>
                <h2 className="text-2xl font-bold text-gray-800">API keys</h2>
                <p className="text-sm text-gray-400 mt-1">
                  Send WhatsApp template messages from your website, CRM or app. Keep keys secret; anyone with a key can
                  send from your number and spend your wallet.
                </p>
              </div>
              <button className={BTN_MAIN} disabled={busy || keys.filter((k) => k.active).length >= access.maxKeys} onClick={createKey}>
                <IoAdd className="text-lg" /> New API key
              </button>
            </div>
            {keys.length === 0 ? (
              <p className="text-sm text-gray-400">No API keys yet.</p>
            ) : (
              <table className="w-full text-sm text-left">
                <thead>
                  <tr className="text-gray-400 border-b text-xs uppercase tracking-wider">
                    <th className="py-3 px-4">Name</th>
                    <th className="py-3 px-4">Key</th>
                    <th className="py-3 px-4">Last used</th>
                    <th className="py-3 px-4 text-right">Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {keys.map((k) => (
                    <tr key={k.id} className={k.active ? "" : "opacity-50"}>
                      <td className="py-3 px-4 font-semibold text-gray-800">{k.name}</td>
                      <td className="py-3 px-4 font-mono text-xs">{k.prefix}</td>
                      <td className="py-3 px-4 text-gray-500">{when(k.lastUsedAt)}</td>
                      <td className="py-3 px-4 text-right">
                        {k.active ? (
                          <button className="text-red-500 font-semibold hover:underline" onClick={() => revoke(k)}>
                            Revoke
                          </button>
                        ) : (
                          <span className="text-xs text-gray-400">Revoked</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          <div className={CARD}>
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
              <div>
                <h2 className="text-2xl font-bold text-gray-800">API campaigns</h2>
                <p className="text-sm text-gray-400 mt-1">
                  Each campaign sends one approved template. Your system sends the campaign name with each message.
                </p>
              </div>
              <button className={BTN_MAIN} onClick={() => setCreatingCampaign(true)}>
                <IoAdd className="text-lg" /> New campaign
              </button>
            </div>
            {campaigns.length === 0 ? (
              <p className="text-sm text-gray-400">No API campaigns yet.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm text-left">
                  <thead>
                    <tr className="text-gray-400 border-b text-xs uppercase tracking-wider">
                      <th className="py-3 px-4">Campaign name</th>
                      <th className="py-3 px-4">Template</th>
                      <th className="py-3 px-4">Sent</th>
                      <th className="py-3 px-4">Failed</th>
                      <th className="py-3 px-4">Status</th>
                      <th className="py-3 px-4 text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-50">
                    {campaigns.map((c) => (
                      <tr key={c.id}>
                        <td className="py-3 px-4 font-mono text-xs font-semibold text-gray-800">{c.name}</td>
                        <td className="py-3 px-4 text-gray-600">
                          {c.templateName} <span className="text-gray-400">({c.language})</span>
                        </td>
                        <td className="py-3 px-4">{c.sentCount}</td>
                        <td className="py-3 px-4">{c.failedCount}</td>
                        <td className="py-3 px-4">
                          <span
                            className={`px-2.5 py-1 rounded-full text-xs font-semibold border ${
                              c.status === "LIVE"
                                ? "bg-green-50 text-green-700 border-green-100"
                                : "bg-gray-50 text-gray-500 border-gray-200"
                            }`}
                          >
                            {c.status === "LIVE" ? "Live" : "Paused"}
                          </span>
                        </td>
                        <td className="py-3 px-4 text-right whitespace-nowrap">
                          <button className="text-brand-main font-semibold hover:underline" onClick={() => toggle(c)}>
                            {c.status === "LIVE" ? "Pause" : "Resume"}
                          </button>
                          <button className="ml-4 text-red-500 font-semibold hover:underline" onClick={() => removeCampaign(c)}>
                            Delete
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          <div className={CARD}>
            <h2 className="text-2xl font-bold text-gray-800">Send a message</h2>
            <p className="text-sm text-gray-500">
              <span className="font-mono text-xs bg-gray-100 rounded px-1.5 py-0.5">POST {apiUrl(access.endpoint)}</span>{" "}
              — the same request body as AiSensy's Campaign API, so integrations built for it only need the new URL and key.
              The key can also go in an <span className="font-mono text-xs">X-API-Key</span> header. Each message is charged
              to your wallet at the template's price, only after WhatsApp accepts it.
            </p>
            <pre className="rounded-2xl bg-[#0b1220] text-slate-200 p-5 text-xs overflow-x-auto">{example}</pre>
            <div className="grid sm:grid-cols-2 gap-3 text-sm">
              <Field name="apiKey" text="Your API key (or the X-API-Key header)." />
              <Field name="campaignName" text="The name of a live API campaign." />
              <Field name="destination" text="Mobile number with country code, digits only." />
              <Field name="templateParams" text="Values for {{1}}, {{2}}… of the template, in order." />
              <Field name="media" text="{ url, filename } for an image, video or document header." />
              <Field name="userName / tags" text="Saved on the contact. New numbers become contacts." />
              <Field name="buttons" text="Button parameters in WhatsApp's format (e.g. a URL suffix)." />
              <Field name="paramsFallbackValue" text="Used, in order, for empty template params." />
            </div>
            <p className="text-sm text-gray-500">
              Success: <span className="font-mono text-xs">{`{"success":true,"submitted_message_id":"wamid…"}`}</span>. Errors:{" "}
              <span className="font-mono text-xs">{`{"success":false,"errorMessage":"…"}`}</span> with 401 (key), 402 (plan or
              balance), 404 (campaign) or 429 (more than 20 calls a second per key).
            </p>
          </div>
        </div>
      )}

      {newKey && <NewKeyDialog apiKey={newKey} onClose={() => setNewKey(null)} />}
      {creatingCampaign && (
        <CampaignDialog
          onClose={() => setCreatingCampaign(false)}
          onCreated={async () => {
            setCreatingCampaign(false);
            await load();
          }}
        />
      )}
    </PageLayout>
  );
}

function Field({ name, text }: { name: string; text: string }) {
  return (
    <div className="rounded-xl bg-gray-50 px-3.5 py-2.5">
      <span className="font-mono text-xs font-semibold text-gray-800">{name}</span>
      <p className="text-xs text-gray-500 mt-0.5">{text}</p>
    </div>
  );
}

function Dialog({ title, onClose, children }: { title: string; onClose: () => void; children: React.ReactNode }) {
  return (
    <div className="fixed inset-0 z-[1000] grid place-items-center bg-black/40 p-4" role="dialog" aria-modal="true" aria-label={title}>
      <div className="w-full max-w-lg rounded-3xl bg-white p-6 shadow-xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-bold text-gray-800">{title}</h3>
          <button onClick={onClose} aria-label="Close" className="p-1.5 rounded-lg hover:bg-gray-100">
            <IoClose className="text-xl" />
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

function NewKeyDialog({ apiKey, onClose }: { apiKey: string; onClose: () => void }) {
  return (
    <Dialog title="Your new API key" onClose={onClose}>
      <p className="text-sm text-gray-500 mb-3">Copy it now: it won't be shown again. If you lose it, revoke it and make a new one.</p>
      <pre className="rounded-2xl bg-gray-50 border border-gray-100 p-4 text-sm font-mono break-all whitespace-pre-wrap">{apiKey}</pre>
      <div className="flex justify-end gap-2 mt-4">
        <button className={BTN_PLAIN} onClick={() => navigator.clipboard.writeText(apiKey).then(() => toast.success("Copied"))}>
          <IoCopyOutline /> Copy
        </button>
        <button className={BTN_MAIN} onClick={onClose}>
          I've saved it
        </button>
      </div>
    </Dialog>
  );
}

function CampaignDialog({ onClose, onCreated }: { onClose: () => void; onCreated: () => void }) {
  const [templates, setTemplates] = useState<MetaTemplate[] | null>(null);
  const [form, setForm] = useState({ name: "", template: "", defaultMediaUrl: "" });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    customFetch
      .get("/api/whatsapp/send/search?page=0&size=100&status=APPROVED")
      .then((res) => {
        let payload = res.data?.data;
        if (typeof payload === "string") payload = JSON.parse(payload);
        const list: MetaTemplate[] = (payload?.results || payload?.data || []).filter(
          (t: MetaTemplate) => !t.status || t.status === "APPROVED",
        );
        setTemplates(list);
      })
      .catch((e) => {
        setTemplates([]);
        setError(msg(e));
      });
  }, []);

  async function save() {
    const [templateName, language] = form.template.split("|");
    setBusy(true);
    setError(null);
    try {
      await customFetch.post("/api/developer/campaigns", {
        name: form.name.trim(),
        templateName,
        language,
        defaultMediaUrl: form.defaultMediaUrl.trim() || undefined,
      });
      toast.success("Campaign created");
      onCreated();
    } catch (e) {
      setError(msg(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog title="New API campaign" onClose={onClose}>
      <div className="grid gap-3">
        <label className="text-sm font-semibold text-gray-700">
          Campaign name
          <input
            className={`${INPUT} mt-1.5 font-mono`}
            placeholder="order_confirmation"
            value={form.name}
            maxLength={120}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
          />
          <span className="block text-xs font-normal text-gray-400 mt-1">Your system sends this as campaignName.</span>
        </label>
        <label className="text-sm font-semibold text-gray-700">
          Approved template
          <select
            className={`${INPUT} mt-1.5`}
            value={form.template}
            onChange={(e) => setForm({ ...form, template: e.target.value })}
            disabled={!templates}
          >
            <option value="">{templates === null ? "Loading templates…" : "Choose a template"}</option>
            {(templates || []).map((t) => (
              <option key={`${t.name}|${t.language}`} value={`${t.name}|${t.language}`}>
                {t.name} ({t.language}){t.category ? ` · ${t.category}` : ""}
              </option>
            ))}
          </select>
        </label>
        <label className="text-sm font-semibold text-gray-700">
          Default header media URL (optional)
          <input
            className={`${INPUT} mt-1.5`}
            placeholder="https://… for templates with an image, video or document header"
            value={form.defaultMediaUrl}
            onChange={(e) => setForm({ ...form, defaultMediaUrl: e.target.value })}
          />
        </label>
        {error && <p className="text-sm text-red-500" role="alert">{error}</p>}
        <div className="flex justify-end gap-2 mt-2">
          <button className={BTN_PLAIN} onClick={onClose}>
            Cancel
          </button>
          <button className={BTN_MAIN} disabled={busy || !form.name.trim() || !form.template} onClick={save}>
            {busy ? "Creating…" : "Create campaign"}
          </button>
        </div>
      </div>
    </Dialog>
  );
}
